import React, { useState } from 'react';
import { MapPin, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

interface LocationCaptureProps {
  onLocationCapture: (latitude: number, longitude: number) => void;
  disabled?: boolean;
}

type LocationStatus = 'idle' | 'loading' | 'success' | 'error';

const LocationCapture: React.FC<LocationCaptureProps> = ({
  onLocationCapture,
  disabled = false
}) => {
  const [status, setStatus] = useState<LocationStatus>('idle');
  const [error, setError] = useState<string>('');
  const [, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);

  const handleCapture = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setStatus('success');
        onLocationCapture(latitude, longitude);
      },
      (error) => {
        let errorMessage = 'Failed to get location';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        setError(errorMessage);
        setStatus('error');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="flex flex-col items-center gap-3 py-2">
            <Loader2 className="text-emerald-500 animate-spin" size={24} />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Getting your location...
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="flex items-center justify-center gap-3 p-3 border-t border-gray-300 dark:border-gray-600">
            <CheckCircle className="text-emerald-500" size={16} />
            <div className="">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Location captured
              </p>
              {/* {location && (
                <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
              )} */}
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="flex flex-col items-center gap-3 py-2">
            <AlertCircle className="text-red-500" size={24} />
            <div className="text-center">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Failed to get location
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {error}
              </p>
            </div>
            <button
              onClick={handleCapture}
              disabled={disabled}
              className="mt-2 px-4 py-2 bg-linear-to-r from-emerald-500 to-teal-600 text-white text-sm rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Try Again
            </button>
          </div>
        );

      case 'idle':
      default:
        return (
          <button
            onClick={handleCapture}
            disabled={disabled}
            className="w-full flex items-center justify-center gap-2 p-3 border-t border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MapPin size={16} />
            <span className="text-sm font-medium">Share My Location</span>
          </button>
        );
    }
  };

  return (
    <div className="">
      {renderContent()}
    </div>
  );
};

export default LocationCapture;
